import React from 'react';
import { Sparkles } from 'lucide-react';
import OrnateDivider from './OrnateDivider';
import EditableText from './EditableText';
import { useAuth } from '../context/AuthContext';
import { authUrlWithNext } from '../lib/whatsapp';
import { useEnquiry } from '../context/EnquiryContext';

// Shared two-option enroll card used across Home, /courses, Dashboard-not-enrolled
// state, and any other page that needs to prompt enrollment.
//
// Option A → Live Classes (WhatsApp enquiry)
// Option B → Foundation Course · redirects to the course page (/courses/1)
//           where the actual price and enroll flow lives.
//
// Pricing is intentionally NOT shown here — it belongs on the course page only.
// `onSelfPacedClick` still lets a caller override the destination if needed;
// otherwise the CTA navigates to `/courses/1`.

const EnrollBlock = ({ onSelfPacedClick, className = '' }) => {
  const { user } = useAuth();
  const { openEnquiry } = useEnquiry();
  const goWA = () => openEnquiry('live');
  const handleSelfPaced = () => {
    if (onSelfPacedClick) {
      onSelfPacedClick();
      return;
    }
    // Default everywhere-else behaviour: redirect to the course page.
    if (!user) {
      window.location.assign(authUrlWithNext(null, '/courses/1'));
      return;
    }
    window.location.assign('/courses/1');
  };

  return (
    <div
      className={`card-mystical rounded-2xl p-8 md:p-10 ${className}`}
      data-testid="enroll-block"
    >
      <OrnateDivider variant="lotus" className="mb-6 max-w-md mx-auto" />

      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-primary/70 text-[11px] uppercase tracking-[0.3em] mb-4">
          <Sparkles size={14} />
          <span>THE SIDDHA METHOD</span>
        </div>
        <h3 className="text-3xl md:text-4xl font-heading font-bold golden-text text-glow tracking-wide mb-3">
          <EditableText id="enroll.heading" defaultValue="Begin the Practice" />
        </h3>
        <p className="text-sm md:text-base text-primary/70 italic max-w-2xl mx-auto">
          <EditableText
            id="enroll.subheading"
            defaultValue="The Siddha Method. Self-paced or live guidance. Community throughout."
          />
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Option A — Live Classes */}
        <div
          className="rounded-xl border border-primary/40 bg-primary/5 p-6 flex flex-col"
          data-testid="enroll-option-live"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-primary/70 mb-3">
            <EditableText id="enroll.live.label" defaultValue="Live Classes" />
          </div>
          <p className="text-sm leading-relaxed text-foreground/85 mb-5 flex-1">
            <EditableText
              id="enroll.live.detail"
              defaultValue="Live online classes and energy exchange with Sanjay Ji, advancing you in the journey of self discovery through mastering Siddha kriyas (beginner friendly)"
            />
          </p>
          <div className="text-sm md:text-base text-primary/80 italic mb-5">
            <EditableText id="liveclasses.cta.text" defaultValue="Personal guidance · Tailored to your practice" />
          </div>
          <button
            type="button"
            onClick={goWA}
            data-testid="enroll-live-cta"
            className="ornate-button px-6 py-3 text-sm font-heading text-primary uppercase tracking-wider rounded-lg"
          >
            <EditableText id="enroll.live.cta" defaultValue="Enquire on WhatsApp →" />
          </button>
        </div>

        {/* Option B — Foundation Course (self-paced) */}
        <div
          className="rounded-xl border border-primary/30 bg-card p-6 flex flex-col"
          data-testid="enroll-option-selfpaced"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-primary/70 mb-3">
            <EditableText id="enroll.selfpaced.label" defaultValue="Foundation Course" />
          </div>
          <p className="text-sm leading-relaxed text-foreground/85 mb-5 flex-1">
            <EditableText
              id="enroll.selfpaced.detail"
              defaultValue="Learn Foundational Siddha Kriyas from our foundational siddha kriya course. Includes one Kriya fine tuning session with Sanjay Ji."
            />
          </p>
          <div className="text-sm md:text-base text-primary/80 italic mb-5">
            <EditableText id="enroll.selfpaced.price" defaultValue="Self paced video lessons on Siddha Kriya" />
          </div>
          <button
            type="button"
            onClick={handleSelfPaced}
            data-testid="enroll-selfpaced-cta"
            className="ornate-button px-6 py-3 text-sm font-heading text-primary uppercase tracking-wider rounded-lg"
          >
            <EditableText id="enroll.selfpaced.cta" defaultValue="Begin Foundation Course" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollBlock;
